import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Button, Spinner, Badge, Form, Modal } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import ThaiDateInput from '../components/ThaiDateInput'
import {
  CONCERN_CATEGORIES, CONCERN_STATUS, IV_ACTIONS, formatThaiDate,
} from "../utils/careUtils";

const fmtDateTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return formatThaiDate(iso);
};

// รายการช่วยเหลือใต้ธง 1 เรื่อง (timeline)
const InterventionList = ({ interventions, onToggleFollowup }) => {
  if (!interventions || interventions.length === 0) {
    return <div className="text-muted fs-sm ps-3">ยังไม่มีบันทึกการช่วยเหลือ</div>;
  }
  return (
    <ul className="list-unstyled mb-0 ps-3">
      {interventions.map((iv) => (
        <li key={iv.iv_id} className="mb-2 border-start ps-3">
          <div className="fs-sm">
            <Badge bg="info" className="me-2">{IV_ACTIONS[iv.iv_action] || iv.iv_action}</Badge>
            <span className="text-muted">{fmtDateTime(iv.created_at)}</span>
            {iv.created_by_name && <span className="text-muted"> · โดย {iv.created_by_name}</span>}
          </div>
          {iv.iv_detail && <div className="fs-sm">{iv.iv_detail}</div>}
          {iv.iv_outcome && <div className="fs-sm text-success">ผล: {iv.iv_outcome}</div>}
          {iv.iv_followup_date && (
            <div className="fs-sm">
              <Form.Check
                type="checkbox"
                id={`fu-${iv.iv_id}`}
                className="d-inline-block"
                checked={!!Number(iv.iv_followup_done)}
                onChange={(e) => onToggleFollowup(iv, e.target.checked)}
                label={<span className={Number(iv.iv_followup_done) ? "text-muted text-decoration-line-through" : ""}>
                  นัดติดตามผล {formatThaiDate(iv.iv_followup_date)}
                </span>}
              />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
};

export default function SubjectCare() {
  const { teach_id } = useParams();
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [, setSkin] = useState(currentSkin);

  const [subject, setSubject] = useState("");
  const [room, setRoom] = useState("");
  const [roster, setRoster] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [showResolved, setShowResolved] = useState(false);

  // modal สร้างธง
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagForm, setFlagForm] = useState({ study_id: "", category: "learning", detail: "" });
  // modal เพิ่มการช่วยเหลือ
  const [ivConcern, setIvConcern] = useState(null);
  const [ivForm, setIvForm] = useState({ iv_action: "counsel", iv_detail: "", iv_outcome: "", iv_followup_date: "" });
  const [submitting, setSubmitting] = useState(false);

  const loadConcerns = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get(`${process.env.REACT_APP_API}/care/concernsByTeach/${teach_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => setConcerns(res.data))
      .catch((err) => {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message });
      })
      .finally(() => setLoading(false));
  }, [teach_id, setLoading]);

  // roster สำหรับ dropdown เลือกนักเรียนตอนสร้างธง (ใช้ behaviorSheet เพราะมีครบแล้ว)
  const loadRoster = useCallback(() => {
    axiosInstance
      .get(`${process.env.REACT_APP_API}/care/behaviorSheet/${teach_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        const students = res.data.students || [];
        if (students.length > 0) {
          const first = students[0];
          setSubject(`${first.sub_subid} ${first.sub_subname}`);
          setRoom(`${first.tb_class_c_id}/${first.r_no}`);
        }
        setRoster(students.filter((s) => s.st_status !== "0"));
      })
      .catch(() => setRoster([]));
  }, [teach_id]);

  useEffect(() => { loadConcerns(); loadRoster(); }, [loadConcerns, loadRoster]);

  const submitFlag = () => {
    const student = roster.find((s) => String(s.study_id) === String(flagForm.study_id));
    if (!student || !flagForm.detail.trim()) {
      Swal.fire({ icon: "warning", title: "กรุณาเลือกนักเรียนและระบุรายละเอียด" });
      return;
    }
    setSubmitting(true);
    axiosInstance
      .post(`${process.env.REACT_APP_API}/care/concern`, {
        teach_id,
        st_id: student.st_id,
        study_id: student.study_id,
        category: flagForm.category,
        detail: flagForm.detail.trim(),
      }, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(() => {
        setShowFlagModal(false);
        setFlagForm({ study_id: "", category: "learning", detail: "" });
        Swal.fire({ title: "บันทึกสำเร็จ", text: "แจ้งครูที่ปรึกษาของห้องแล้ว", icon: "success", timer: 1500, showConfirmButton: false });
        loadConcerns();
      })
      .catch((err) => {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message });
      })
      .finally(() => setSubmitting(false));
  };

  const submitIntervention = () => {
    if (!ivConcern) return;
    setSubmitting(true);
    axiosInstance
      .post(`${process.env.REACT_APP_API}/care/intervention`, {
        concern_id: ivConcern.concern_id,
        iv_action: ivForm.iv_action,
        iv_detail: ivForm.iv_detail.trim() || null,
        iv_outcome: ivForm.iv_outcome.trim() || null,
        iv_followup_date: ivForm.iv_followup_date || null,
      }, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(() => {
        setIvConcern(null);
        setIvForm({ iv_action: "counsel", iv_detail: "", iv_outcome: "", iv_followup_date: "" });
        Swal.fire({ title: "บันทึกสำเร็จ", icon: "success", timer: 1200, showConfirmButton: false });
        loadConcerns();
      })
      .catch((err) => {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message });
      })
      .finally(() => setSubmitting(false));
  };

  const changeStatus = (concern, status) => {
    const label = CONCERN_STATUS[status]?.label || status;
    Swal.fire({
      title: `เปลี่ยนสถานะเป็น "${label}" ?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((res) => {
      if (!res.isConfirmed) return;
      axiosInstance
        .put(`${process.env.REACT_APP_API}/care/concernStatus/${concern.concern_id}`, { status }, {
          headers: { Authorization: `Bearer ${getToken()}` },
        })
        .then(() => loadConcerns())
        .catch((err) => {
          Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message });
        });
    });
  };

  const toggleFollowup = (iv, done) => {
    axiosInstance
      .put(`${process.env.REACT_APP_API}/care/followupDone/${iv.iv_id}`, { done }, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then(() => loadConcerns())
      .catch((err) => {
        Swal.fire({ icon: "error", title: "เกิดข้อผิดพลาด", text: err.response?.data?.message || err.message });
      });
  };

  const active = concerns.filter((c) => c.concern_status !== "resolved");
  const resolved = concerns.filter((c) => c.concern_status === "resolved");
  const shown = showResolved ? concerns : active;

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/teaching/subject-class">วิชา / ห้องที่สอน</Link></li>
              <li className="breadcrumb-item active">การแจ้งดูแลนักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">การแจ้งดูแลนักเรียน (รายวิชา)</h4>
            <p className="text-muted mb-0 fs-sm">{subject} ห้อง {room}</p>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            {/* ปิดชั่วคราว: ระบบการประเมินพฤติกรรมการเรียน (รอบ 2 สัปดาห์)
            <Link to={`/teaching/behavior-assessment/${teach_id}`} className="btn btn-white btn-sm">
              <i className="ri-survey-line me-1"></i>ประเมินพฤติกรรมการเรียน
            </Link>
            */}
            <Button variant="primary" size="sm" onClick={() => setShowFlagModal(true)}>
              <i className="ri-flag-2-line me-1"></i>แจ้งดูแลนักเรียน
            </Button>
          </div>
        </div>

        <Row className="g-12 mb-4">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <Card.Title as="h6" className="mb-0">รายการแจ้งดูแล</Card.Title>
                  <Badge bg="danger">{active.length}</Badge>
                  <small className="text-muted">กำลังดูแล · ยุติแล้ว {resolved.length} เรื่อง</small>
                </div>
                <Form.Check
                  type="switch"
                  id="show-resolved"
                  label="แสดงที่ยุติแล้ว"
                  checked={showResolved}
                  onChange={(e) => setShowResolved(e.target.checked)}
                />
              </Card.Header>
              <Card.Body>
                {shown.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    ยังไม่มีรายการแจ้งดูแลในวิชานี้ — กด "แจ้งดูแลนักเรียน" เมื่อพบนักเรียนที่ต้องการความช่วยเหลือ
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {shown.map((c) => {
                      const status = CONCERN_STATUS[c.concern_status] || {};
                      const isOpen = !!expanded[c.concern_id];
                      return (
                        <div key={c.concern_id} className="border rounded p-3">
                          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                            <div>
                              <div className="fw-semibold">
                                {c.st_name} <span className="text-muted fs-sm">({c.st_idnumber})</span>
                              </div>
                              <div className="fs-sm mt-1">
                                <Badge bg="secondary" className="me-2">{CONCERN_CATEGORIES[c.concern_category] || c.concern_category}</Badge>
                                <Badge bg={status.variant || "secondary"}>{status.label || c.concern_status}</Badge>
                                <span className="text-muted ms-2">
                                  แจ้งเมื่อ {fmtDateTime(c.created_at)}{c.created_by_name && ` โดย ${c.created_by_name}`}
                                </span>
                              </div>
                              <div className="fs-sm mt-2">{c.concern_detail}</div>
                              {c.concern_status === "resolved" && (
                                <div className="fs-xs text-muted mt-1">
                                  ยุติเมื่อ {fmtDateTime(c.resolved_at)}{c.resolved_by_name && ` โดย ${c.resolved_by_name}`}
                                </div>
                              )}
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                              <Button variant="white" size="sm"
                                onClick={() => setExpanded((p) => ({ ...p, [c.concern_id]: !isOpen }))}>
                                <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line me-1`} />
                                การช่วยเหลือ ({c.interventions.length})
                              </Button>
                              {c.concern_status !== "resolved" && (
                                <>
                                  <Button variant="outline-info" size="sm" onClick={() => setIvConcern(c)}>
                                    <i className="ri-hand-heart-line me-1" />บันทึกการช่วยเหลือ
                                  </Button>
                                  <Button variant="outline-success" size="sm" onClick={() => changeStatus(c, "resolved")}>
                                    <i className="ri-check-double-line me-1" />ยุติเรื่อง
                                  </Button>
                                </>
                              )}
                              {c.concern_status === "resolved" && (
                                <Button variant="outline-warning" size="sm" onClick={() => changeStatus(c, "in_progress")}>
                                  <i className="ri-arrow-go-back-line me-1" />เปิดเรื่องอีกครั้ง
                                </Button>
                              )}
                            </div>
                          </div>
                          {isOpen && (
                            <div className="mt-3 pt-3 border-top">
                              <InterventionList interventions={c.interventions} onToggleFollowup={toggleFollowup} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Footer />
      </div>

      {/* Modal: สร้างธงแจ้งดูแล */}
      <Modal show={showFlagModal} onHide={() => setShowFlagModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h6">แจ้งดูแลนักเรียน</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>นักเรียน</Form.Label>
            <Form.Select value={flagForm.study_id}
              onChange={(e) => setFlagForm((p) => ({ ...p, study_id: e.target.value }))}>
              <option value="">— เลือกนักเรียน —</option>
              {roster.map((s) => (
                <option key={s.study_id} value={s.study_id}>{s.st_idnumber} {s.st_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>ประเภทข้อกังวล</Form.Label>
            <Form.Select value={flagForm.category}
              onChange={(e) => setFlagForm((p) => ({ ...p, category: e.target.value }))}>
              {Object.entries(CONCERN_CATEGORIES).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>รายละเอียด</Form.Label>
            <Form.Control as="textarea" rows={3} value={flagForm.detail}
              placeholder="อธิบายสิ่งที่พบ เช่น ไม่ส่งงาน 3 ชิ้นติดต่อกัน หลับในคาบบ่อย ..."
              onChange={(e) => setFlagForm((p) => ({ ...p, detail: e.target.value }))} />
          </Form.Group>
          <div className="fs-xs text-muted mt-2">
            <i className="ri-information-line me-1" />ระบบจะแจ้งเตือนครูที่ปรึกษาของห้องโดยอัตโนมัติ
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowFlagModal(false)}>ยกเลิก</Button>
          <Button variant="primary" size="sm" onClick={submitFlag} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : "บันทึก"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: บันทึกการช่วยเหลือ */}
      <Modal show={!!ivConcern} onHide={() => setIvConcern(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h6">บันทึกการช่วยเหลือ — {ivConcern?.st_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>การดำเนินการ</Form.Label>
            <Form.Select value={ivForm.iv_action}
              onChange={(e) => setIvForm((p) => ({ ...p, iv_action: e.target.value }))}>
              {Object.entries(IV_ACTIONS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Form.Select>
            {ivForm.iv_action === "notify_advisor" && (
              <div className="fs-xs text-muted mt-1">
                <i className="ri-information-line me-1" />ระบบจะส่งการแจ้งเตือนถึงครูที่ปรึกษาของห้อง
              </div>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>รายละเอียด</Form.Label>
            <Form.Control as="textarea" rows={2} value={ivForm.iv_detail}
              onChange={(e) => setIvForm((p) => ({ ...p, iv_detail: e.target.value }))} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>ผลลัพธ์ (ถ้ามี)</Form.Label>
            <Form.Control as="textarea" rows={2} value={ivForm.iv_outcome}
              onChange={(e) => setIvForm((p) => ({ ...p, iv_outcome: e.target.value }))} />
          </Form.Group>
          <Form.Group>
            <Form.Label>นัดติดตามผล (ถ้ามี)</Form.Label>
            <ThaiDateInput value={ivForm.iv_followup_date}
              onChange={v => setIvForm((p) => ({ ...p, iv_followup_date: v }))} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setIvConcern(null)}>ยกเลิก</Button>
          <Button variant="primary" size="sm" onClick={submitIntervention} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : "บันทึก"}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
